import { Injectable } from '@angular/core';
import { Storage } from '@ionic/storage-angular';
import { LocationHierarchyEntity } from '@smatchy/sdk';
import { LocationAdminLevel } from '../utils/resolve-admin-level.util';

const STORAGE_KEY = 'location_search_history';
const MAX_HISTORY = 5;

export interface LocationHistoryItem {
  formattedAddress: string;
  name: string;
  city: string;
  country: string;
  region?: string;
  position: { lat: number; lng: number };
  placeId?: string;
  adminLevel?: LocationAdminLevel;
  selectedLevel?: LocationAdminLevel;
  selectedName?: string;
  countryCode?: string;
  hierarchy?: LocationHierarchyEntity;
  suggestionLabel?: string;
  /** Secondary muted line: "Paris, Île-de-France, France" */
  suggestionDetail?: string;
}

@Injectable({ providedIn: 'root' })
export class LocationHistoryService {
  private storageReady: Promise<void>;

  constructor(private readonly storage: Storage) {
    this.storageReady = this.storage.create().then(() => undefined);
  }

  private async ensureReady() {
    await this.storageReady;
  }

  async getHistory(): Promise<LocationHistoryItem[]> {
    await this.ensureReady();
    const raw = await this.storage.get(STORAGE_KEY);
    return raw ? JSON.parse(raw) : [];
  }

  async addToHistory(item: LocationHistoryItem): Promise<void> {
    await this.ensureReady();
    let history = await this.getHistory();
    history = history.filter(
      (entry) => entry.formattedAddress !== item.formattedAddress,
    );
    history.unshift(item);
    if (history.length > MAX_HISTORY) {
      history = history.slice(0, MAX_HISTORY);
    }
    await this.storage.set(STORAGE_KEY, JSON.stringify(history));
  }

  async removeFromHistory(item: LocationHistoryItem): Promise<void> {
    await this.ensureReady();
    let history = await this.getHistory();
    history = history.filter(
      (entry) => entry.formattedAddress !== item.formattedAddress,
    );
    await this.storage.set(STORAGE_KEY, JSON.stringify(history));
  }

  async clearHistory(): Promise<void> {
    await this.ensureReady();
    await this.storage.remove(STORAGE_KEY);
  }
}
